import { useCallback, useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { useReactFlow } from 'reactflow';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import { EL_COLORS } from '@/colors';
import { ByokBadge } from '@/components/Common/ByokBadge';
import { useWorkflowStore } from '@/state/workflow.state';
import { Flex, FlexCenVer } from '@/UI/styles';
import { getNodeIcon } from '@/components/Nodes/utils/NodeIcon';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { getModelPriceInfo } from '../../Nodes/Utils';
import { NodeDetails } from './NodeDetails';
import type { Node } from '@/types/node';
import type { ModelBaseNodeData } from '@/types/nodes/model';

const MAX_ZOOM = 0.8;
const FIT_VIEW_DURATION = 1000;
const PADDING = 0.2;

type ModelNode = Node & { data: ModelBaseNodeData };

interface NodePropertiesProps {
  node: Node;
  selectedNodes: Node[];
}

function shouldShowPrice(node: Node) {
  return node.isModel && node.type !== 'masks';
}

export const NodeProperties = ({ node: propNode, selectedNodes }: NodePropertiesProps) => {
  const modelPricesMaps = useWorkflowStore((state) => state.modelPricesMaps);
  const workspaceFalByokEnabled = useWorkflowStore((state) => state.workspaceFalByokEnabled);
  const modelPriceLookupOptions = useMemo(() => ({ workspaceFalByokEnabled }), [workspaceFalByokEnabled]);
  const getNodePriceInfo = useCallback(
    (node: Node) => {
      if (!node.isModel) {
        return undefined;
      }
      const modelNode = node as ModelNode;
      return getModelPriceInfo(
        modelNode.data.model || {},
        modelPricesMaps,
        modelNode.data.params,
        modelPriceLookupOptions,
      );
    },
    [modelPriceLookupOptions, modelPricesMaps],
  );

  const { fitView } = useReactFlow();
  const node = propNode as ModelNode;
  const nodePriceInfo = useMemo(() => getNodePriceInfo(node), [getNodePriceInfo, node]);
  const nodeIsByok = nodePriceInfo?.isByok === true;
  const onDoubleClickNodeName = () => {
    fitView({ nodes: [{ id: node.id }], maxZoom: MAX_ZOOM, duration: FIT_VIEW_DURATION, padding: PADDING });
  };
  return (
    <Box key={node.id}>
      {selectedNodes.length > 1 ? (
        <Accordion sx={{ background: 'none', borderBottom: `1px solid ${EL_COLORS.BoxBorder}` }} elevation={0}>
          <AccordionSummary
            expandIcon={<CaretIcon />}
            aria-controls="panel1-content"
            data-testid={`properties-drawer-item-${node.id}`}
            sx={{
              '& .MuiAccordionSummary-content': {
                minWidth: 0,
                overflow: 'hidden',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                ml: 1,
              },
            }}
          >
            <FlexCenVer sx={{ gap: 1, justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
              <Flex sx={{ gap: 1, minWidth: 0, flex: 1, alignItems: 'center', overflow: 'hidden' }}>
                {getNodeIcon(node.data.model, '16px')}
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <EllipsisText
                    data-testid={`properties-node-name-${node.id}`}
                    variant="body-sm-rg"
                    maxWidth="100%"
                    delay={800}
                    sx={{ display: 'block', width: '100%', minWidth: 0 }}
                  >
                    {node.data.name}
                  </EllipsisText>
                </Box>
              </Flex>
              {shouldShowPrice(node) || nodeIsByok ? (
                <FlexCenVer sx={{ gap: 1, flexShrink: 0, ml: 0.5, alignItems: 'center' }}>
                  {shouldShowPrice(node) && !nodeIsByok ? (
                    <>
                      <AsteriskIcon width={16} height={16} />
                      <Typography variant="body-sm-rg">{nodePriceInfo?.credits}</Typography>
                    </>
                  ) : null}
                  {nodeIsByok ? <ByokBadge /> : null}
                </FlexCenVer>
              ) : null}
            </FlexCenVer>
          </AccordionSummary>
          <AccordionDetails>
            <NodeDetails node={node} />
          </AccordionDetails>
        </Accordion>
      ) : (
        <Box>
          <FlexCenVer sx={{ mb: 2, justifyContent: 'space-between', minWidth: 0, width: '100%' }}>
            <Flex
              sx={{ gap: 1, alignItems: 'center', minWidth: 0, flex: 1, overflow: 'hidden', cursor: 'pointer' }}
              onDoubleClick={onDoubleClickNodeName}
            >
              {getNodeIcon(node.data.model, '16px')}
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <EllipsisText
                  data-testid={`properties-node-name-${node.id}`}
                  variant="body-sm-rg"
                  maxWidth="100%"
                  delay={800}
                  sx={{ display: 'block', width: '100%', minWidth: 0 }}
                >
                  {node.data.name}
                </EllipsisText>
              </Box>
            </Flex>
            {shouldShowPrice(node) || nodeIsByok ? (
              <FlexCenVer sx={{ gap: 1, alignItems: 'center', flexShrink: 0, ml: 0.5 }}>
                {shouldShowPrice(node) && !nodeIsByok ? (
                  <>
                    <AsteriskIcon width={16} height={16} />
                    <Typography variant="body-sm-rg">{nodePriceInfo?.credits}</Typography>
                  </>
                ) : null}
                {nodeIsByok ? <ByokBadge /> : null}
              </FlexCenVer>
            ) : null}
          </FlexCenVer>
          <NodeDetails node={node} />
        </Box>
      )}
    </Box>
  );
};
